<script lang="ts" setup>
import { MoneyFormatter } from '@apps/main/new-desktop/core/components/formatters';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppStandardModal, AppTag } from '@libs/ui';
import { SemboDate, DateFormat } from '@libs/sembo-dates';

import FlightSegment from './FlightSegment.vue';
import FlightStopover from './FlightStopover.vue';
import FlightRules from './FlightRules.vue';
import type {
  FlightDetailsModalData,
  TransportTag,
} from './flightDetailsModal.model';

interface FlightDetailsModalProps {
  flightData: FlightDetailsModalData;
  imageHandler: (src: string) => void;
  pax: string;
}

const props = defineProps<FlightDetailsModalProps>();

interface FlightDetailsModalEmits {
  (eventName: 'close', context: void): void;
}

const emit = defineEmits<FlightDetailsModalEmits>();

const { t } = i18n.global;

function getTransportTags(): TransportTag[] {
  const tags: TransportTag[] = [];

  if (props.flightData) {
    const departureDate = new SemboDate(props.flightData?.departureDate);
    const arrivalDate = new SemboDate(props.flightData?.arrivalDate);
    const isTransportArrivingDifferentDay = !departureDate.isSameDay(
      arrivalDate.get()
    );

    if (isTransportArrivingDifferentDay) {
      const title = `${t(
        'planner.this_flight_arrives_on'
      )} ${arrivalDate.format(DateFormat.DateMonth)}`;

      const transportTag: TransportTag = {
        type: 'transport',
        iconName: 'calendarOutlined',
        title,
      };

      tags.push(transportTag);
    }
  }

  return tags;
}
</script>

<template>
  <AppStandardModal @close="emit('close')">
    <template #title>{{ $t('planner.transport.flight') }}</template>

    <template #subtitle>
      {{
        `${props.flightData.title.fromAirport?.name} - ${props.flightData.title.toAirport?.name}`
      }}
    </template>

    <template #body>
      <div class="flight-details-modal-content">
        <div v-for="(segment, index) in props.flightData.segments" :key="index">
          <FlightSegment
            :segment="segment"
            :image-handler="props.imageHandler"
          />
          <FlightStopover
            v-if="segment.stopver > 0"
            :stopover="segment.stopver"
          />
        </div>

        <div class="tags-container">
          <AppTag
            v-for="(tag, index) in getTransportTags()"
            :key="index"
            type="warning"
            :icon="{ name: tag.iconName, size: '13' }"
          >
            <template #title>
              <span class="tag-text">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                {{ $t(tag.title) }}
              </span>
            </template>
          </AppTag>
        </div>

        <FlightRules :token="props.flightData.addToBasketToken" />
      </div>
    </template>
    <template #summary>
      <div class="flight-details-modal-footer">
        <div>
          <MoneyFormatter
            :data="{
              price: props.flightData.price.amount,
              minPrice: undefined,
              font: 'font-label-xl-bold',
              color: 'color-secondary-40',
            }"
          />
        </div>
        <div class="summary-pax">
          {{ $t('planner.total_for') }} {{ props.pax }}
        </div>
      </div>
    </template>
  </AppStandardModal>
</template>

<style lang="scss" scoped>
.flight-details-modal-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-8);

  .tags-container {
    margin-top: var(--spacing-8);
  }
}
.flight-details-modal-footer {
  display: flex;
  flex-direction: column;
  .summary-pax {
    padding-top: var(--spacing-4);
    color: var(--color-secondary-70);
    @include font-label-s-light;
  }
}
</style>
